import axios, { AxiosRequestConfig, AxiosInstance } from 'axios';
import helper from '@/util/case-helper';
import { currentPath, handleAxiosError, routeUpdate } from './error-handler';
import store from '@/store';
import { ActionType, Routes } from '@/constants';
import { getIdToken } from '@/core';
import { bus } from '@/main';

export let source = axios.CancelToken.source();

export const generateNewToken = () => {
  source = axios.CancelToken.source();
}

const setAdditionalHeaders = () => ({
  Accept: 'application/json, text/plain, */*',
  'Content-Type': 'application/json;charset=utf-8',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
})

const axiosInstance = (config: AxiosRequestConfig, token?: any): AxiosInstance => {
  const axiosConfig: AxiosRequestConfig = {
    baseURL: process.env.VUE_APP_BASE_URL,
    ...config,
    headers: {
      ...token,
      ...config.headers,
      ...setAdditionalHeaders()
    }
  };
  return axios.create(axiosConfig);
}

const transformResponse = async (_axios: any, isSnakeCase = true, isCancel = false) => _axios
  .then(async (res: any) => {
    store.getters.notFound && store.dispatch(ActionType.UPDATE_NOT_FOUND_STATUS, false);
    if (isSnakeCase) {
      return helper.snakeToCamel(res.data);
    } else {
      return res.data;
    }
  })
  .catch((err: any) => {
    if (axios.isCancel(err)) {
      generateNewToken();
      if (!isCancel) !currentPath(Routes.SELECT_HOME) && routeUpdate(Routes.SELECT_HOME);
    } else {
      handleAxiosError(err.response);
    }
  });

const get = async (url: string, config: any, isSnakeCase = true) => {
  const token = await getIdToken();
  return transformResponse(axiosInstance({ ...config, cancelToken: source.token }, token).get(url), isSnakeCase);
}

const getStream = async (url: string, config: any) => {
  const token: any = await getIdToken();
  const axiosConfig: AxiosRequestConfig = {
    baseURL: process.env.VUE_APP_BASE_URL,
    ...config,
    headers: {
      cancelToken: source.token,
      ...token,
      ...config.headers
    }
  };
  axiosConfig.headers['Content-Type'] = 'application/json;charset=utf-8';
  axiosConfig.responseType = 'arraybuffer';
  const instance = axios.create(axiosConfig);
  return instance.get(url);
}

const post = async (url: string, data: any, config: any, isSnakeCase = true, isCancel = false) => {
  const token = await getIdToken();
  return transformResponse(axiosInstance({ ...config, cancelToken: source.token }, token).post(url, helper.camelToSnake(data)), isSnakeCase, true);
}
const rawPost = async (url: string, data: any, config: any, isSnakeCase = true) => {
  const token = await getIdToken();
  const response = await axiosInstance({ ...config, cancelToken: source.token }, token).post(url, helper.camelToSnake(data));
  if (isSnakeCase) {
    return helper.snakeToCamel(response.data);
  } else {
    return response.data;
  }
}

const deleteAttachments = async (url: string, data: any, config: any, isSnakeCase = true, isCancel = false, retry = false) => {
  const token = await getIdToken();
  return transformResponse(axiosInstance({ ...config, cancelToken: source.token }, token).delete(url, { data }));
}

const postStream = async (url: string, data: any, config: any) => {
  return axiosInstance({ ...config, cancelToken: source.token }).post(url, helper.camelToSnake(data));
}

const put = async (url: string, data: any, config: any) => {
  const token = await getIdToken();
  return transformResponse(axiosInstance({ ...config, cancelToken: source.token }, token).put(url, helper.camelToSnake(data)));
}

const del = async (url: string, config: any) => {
  const token = await getIdToken();
  return transformResponse(axiosInstance({ ...config, cancelToken: source.token }, token).delete(url));
}

const deleteStream = async (url: string, data: any, config: any) => {
  const token = await getIdToken();
  return transformResponse(
    axiosInstance({ ...config, cancelToken: source.token }, token)
      .delete(url, {
        data: helper.camelToSnake(data)
      })
  )
};

const patch = async (url: string, data: any, config: any) => {
  const token = await getIdToken();
  return transformResponse(axiosInstance({ ...config, cancelToken: source.token }, token).patch(url, helper.camelToSnake(data)));
}

const cancelRequest = () => {
  source.cancel();
  generateNewToken();
}

export default { get, getStream, post, postStream, put, patch, delete: del, source, generateNewToken, deleteStream, rawPost, deleteAttachments, cancelRequest };
